/*
 * Copyright 2020, IntraLinks, Inc. All rights reserved.
 */

import {
  configureStore, ThunkAction, Action, combineReducers
} from '@reduxjs/toolkit';
import { logger } from 'redux-logger';
import { coreAppReducer } from '@intralinks/apps-core';
import { createBrowserHistory } from 'history';
import { routerMiddleware, connectRouter } from 'connected-react-router';

import profileReducer from '../features/Profile/profileSlice';

import hostAppReducer from '../features/ErrorPages/errorPageSlice';
import enrollReducer from '../features/Profile/EnrollMFA/enrollSlice';
import { ENDPOINT_PREFIX } from './shared/utils/constants.util';

import consentReducer from '../features/Consent/ConsentSlice';
import appSlice from './shared/AppSlice/AppSlice';
import emailPreferencesSlice from '../features/Profile/EmailPreferences/EmailPreferencesSlice';
import updatePasswordReducer from '../features/Profile/UpdatePassword/updatePasswordSlice';
import setupSecurityReducer from '../features/SecuritySetup/setupSecuritySlice';
import profileEnrollReducer from '../features/Profile/LoAFactors/profileEnrollSlice';

import { getConfig } from './shared/utils/config.util';
import { ENV } from '../config/environments';

export const routerHistory = createBrowserHistory({
  basename: ENDPOINT_PREFIX
});

const rootReducer = combineReducers({
  router: connectRouter(routerHistory),
  coreApp: coreAppReducer,
  profile: profileReducer,
  app: hostAppReducer,
  appState: appSlice,
  emailPreferencesSettings: emailPreferencesSlice,
  enroll: enrollReducer,
  updatePassword: updatePasswordReducer,
  consent: consentReducer,
  setupSecurity: setupSecurityReducer,
  profileEnroll: profileEnrollReducer
});

// Making sure that if there are any places where getConfig is not returning the expected values
// there are no logs exposed. For this reason the default env is set to Production
const NODE_ENV = getConfig()?.NODE_ENV
  ? getConfig().NODE_ENV
  : (globalThis?.global?.process?.env?.NODE_ENV) || ENV.Production;

const shouldUseDevTools = (NODE_ENV !== ENV.Production && NODE_ENV !== ENV.Preview && NODE_ENV !== ENV.Test);

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    const middlewares = getDefaultMiddleware({
      serializableCheck: false
    });
    if (shouldUseDevTools) {
      middlewares.push(logger);
    }
    middlewares.push(routerMiddleware(routerHistory));
    return middlewares;
  },
  devTools: shouldUseDevTools
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
